import { Link } from "react-router-dom";
import { YoutubeLogo, InstagramLogo, XLogo } from "@phosphor-icons/react";

const LOGO = "https://customer-assets.emergentagent.com/job_admin-dashboard-flux/artifacts/ef1ryefo_The%20Gowda%E2%80%99s%20Group.png";

const SOCIAL_LINKS = [
  {
    label: "YouTube",
    handle: "VeloxAlgoTech LLP",
    href: "https://www.youtube.com/channel/UCAcFvQSqDfC9X3B3J-aRIIA",
    Icon: YoutubeLogo,
    testid: "footer-social-youtube",
    color: "#ff0033",
  },
  {
    label: "Instagram",
    handle: "@veloxalgotech_llp",
    href: "https://www.instagram.com/veloxalgotech_llp?igsh=MWRqOXcwOXFyazJpMQ==",
    Icon: InstagramLogo,
    testid: "footer-social-instagram",
    color: "#E4405F",
  },
  {
    label: "Twitter / X",
    handle: "@veloxalgotechll",
    href: "https://x.com/veloxalgotechll",
    Icon: XLogo,
    testid: "footer-social-twitter",
    color: "#ffffff",
  },
];

const LEGAL_LINKS = [
  { label: "Terms of Use", to: "/legal/terms", testid: "footer-legal-terms" },
  { label: "Privacy Policy", to: "/legal/privacy", testid: "footer-legal-privacy" },
  { label: "Refund Policy", to: "/legal/refund", testid: "footer-legal-refund" },
  { label: "Services Agreement", to: "/legal/services", testid: "footer-legal-services" },
];

const SUPPORT_LINKS = [
  { label: "support@veloxalgotech.com", to: "mailto:support@veloxalgotech.com", external: true, testid: "footer-support-email" },
  { label: "Admin Login", to: "/login", testid: "footer-admin-login" },
  { label: "Login User Portal", to: "/affiliate/login", testid: "footer-user-login" },
];

/**
 * Global footer rendered on every page of the app (public + authenticated).
 * Sticks to the natural bottom of the page (not fixed) so it doesn't cover content.
 */
export default function GlobalFooter() {
  return (
    <footer
      data-testid="global-footer"
      className="mt-auto w-full border-t border-white/10 bg-[#0b132b]/95 text-zinc-300"
      style={{ fontFamily: "'Manrope', 'Inter', sans-serif" }}
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Brand block */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <img src={LOGO} alt="VeloxAlgoTech LLP" className="w-14 h-14 rounded-full ring-1 ring-[#00b4d8]/30" data-testid="footer-logo" />
              <span
                className="text-xs md:text-sm font-extrabold tracking-[0.14em] leading-[1.1] flex flex-col"
                style={{ fontFamily: "'Outfit', sans-serif" }}
                data-testid="footer-brand-name"
              >
                <span style={{ color: '#00b4d8' }}>Velox</span>
                <span className="text-white">AlgoTech LLP</span>
              </span>
            </div>
            <p className="text-[11px] leading-relaxed text-zinc-400 max-w-xs">
              AI-powered automated forex trading for beginners to pros. A product of Velox Algotech.
            </p>

            {/* Social icons */}
            <div className="mt-5">
              <div
                className="text-[10px] font-semibold tracking-[0.28em] text-[#00b4d8] mb-3"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              >
                FOLLOW US
              </div>
              <div className="flex items-center gap-3" data-testid="footer-social-row">
                {SOCIAL_LINKS.map(({ label, handle, href, Icon, testid, color }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    title={`${label} · ${handle}`}
                    data-testid={testid}
                    className="group flex items-center justify-center w-10 h-10 rounded-full border border-white/10 bg-white/[0.02] hover:bg-white/[0.06] hover:border-white/25 transition-all"
                    style={{ "--brand": color }}
                  >
                    <Icon
                      size={17}
                      weight="fill"
                      className="text-zinc-300 group-hover:text-white transition-colors"
                      style={{ color: "var(--brand, currentColor)" }}
                    />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Legal column */}
          <div>
            <h4
              className="text-[10px] font-semibold tracking-[0.28em] text-[#00b4d8] mb-4"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              LEGAL
            </h4>
            <ul className="space-y-2.5">
              {LEGAL_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={l.testid}
                    className="text-[12px] text-zinc-300 hover:text-white transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support column */}
          <div>
            <h4
              className="text-[10px] font-semibold tracking-[0.28em] text-[#00b4d8] mb-4"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              SUPPORT
            </h4>
            <ul className="space-y-2.5">
              {SUPPORT_LINKS.map((l) =>
                l.external ? (
                  <li key={l.to}>
                    <a
                      href={l.to}
                      data-testid={l.testid}
                      className="text-[12px] text-zinc-300 hover:text-white transition-colors break-all"
                    >
                      {l.label}
                    </a>
                  </li>
                ) : (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      data-testid={l.testid}
                      className="text-[12px] text-zinc-300 hover:text-white transition-colors"
                    >
                      {l.label}
                    </Link>
                  </li>
                ),
              )}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-8 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3">
          <p
            className="text-[10px] tracking-[0.14em] text-zinc-500 text-center md:text-left"
            data-testid="footer-copyright"
          >
            © 2026 VELOX ALGOTECH LLP · VELOX ALGO AI · RISK DISCLOSURE · TRADING INVOLVES SIGNIFICANT RISK
          </p>
        </div>
      </div>
    </footer>
  );
}
